import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";

/**
 * Mehrfachauswahl aus einem FESTEN Vokabular — Chips zum An- und Abwählen.
 *
 * Abgrenzung zu den beiden bestehenden Widgets: `TagInput` nimmt Freitext (die
 * Menge ist offen), `Sel` lässt genau einen Wert zu. Hier ist die Liste
 * vorgegeben und mehrere Werte sind der Normalfall — eine Firma ist oft
 * Betriebs- UND Besitzgesellschaft.
 *
 * Persistiert wie `TagInput` als EIN String mit „; " verbunden, damit der Wert
 * in eine gewöhnliche varchar-Spalte passt und ohne Join lesbar bleibt.
 */

const SEP = "; ";

const useLocal = makeStyles({
  box: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "8px",
    rowGap: "8px",
    minHeight: "44px",
    ...shorthands.padding("6px", "0"),
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("7px", "13px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink2,
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "130ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  chipOn: {
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    ...shorthands.borderColor(REFINED.aub),
  },
  /** Nur-Lese-Zustand: keine Klickfläche, abgewählte Optionen entfallen ganz. */
  chipStatic: {
    cursor: "default",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  mark: {
    display: "inline-flex",
    width: "14px",
    height: "14px",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
    lineHeight: 1,
  },
  leer: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    color: REFINED.ink3,
  },
  hint: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    color: REFINED.ink3,
  },
});

export function OptionChips({
  label,
  name,
  options,
  value,
  onChange,
  hint,
  hintUnterLabel,
}: {
  label: string;
  name: string;
  /**
   * Das Vokabular. Ein Eintrag ist entweder der Wert selbst oder ein Paar aus
   * gespeichertem Wert und abweichender Beschriftung — gebraucht dort, wo der
   * Anzeigetext länger ist als der Wert in der Spalte („Tippgeber" steht als
   * „Tippgeber/Vermittler" auf dem Chip).
   */
  options: readonly (string | { value: string; label: string })[];
  /** Gewählte Werte als „a; b"-String (leer = nichts gewählt). */
  value: string;
  /** Fehlt der Handler, ist das Feld nur lesbar. */
  onChange?: (v: string) => void;
  hint?: string;
  /** Hinweis direkt unter dem Label statt unter den Chips. */
  hintUnterLabel?: boolean;
}) {
  const s = useFormPrimitiveStyles();
  const l = useLocal();

  const gewaehlt = React.useMemo(
    () => (value ? value.split(/[;,]/).map((t) => t.trim()).filter(Boolean) : []),
    [value],
  );

  const norm = React.useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options],
  );

  const toggle = (opt: string) => {
    if (!onChange) return;
    // Reihenfolge des VOKABULARS, nicht die der Klicks: sonst steht dieselbe
    // Auswahl je nach Bedienreihenfolge anders in der Spalte und liest sich in
    // Listen uneinheitlich.
    const next = gewaehlt.includes(opt)
      ? gewaehlt.filter((g) => g !== opt)
      : norm.filter((o) => o.value === opt || gewaehlt.includes(o.value)).map((o) => o.value);
    onChange(next.join(SEP));
  };

  // Nur-Lese: abgewählte Optionen wegzulassen ist ehrlicher, als sie grau
  // anzubieten — anklicken ließe sie sich ohnehin nicht.
  const sichtbar = onChange ? norm : norm.filter((o) => gewaehlt.includes(o.value));

  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={name}>{label}</label>
      {hint && hintUnterLabel && <span className={l.hint}>{hint}</span>}
      <div className={l.box} id={name} role="group" aria-label={label}>
        {sichtbar.length === 0 && <span className={l.leer}>nicht erfasst</span>}
        {sichtbar.map((opt) => {
          const on = gewaehlt.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              role="checkbox"
              aria-checked={on}
              disabled={!onChange}
              className={mergeClasses(l.chip, on && l.chipOn, !onChange && l.chipStatic)}
              onClick={() => toggle(opt.value)}
            >
              <span className={l.mark} aria-hidden>{on ? "✓" : "+"}</span>
              {opt.label}
            </button>
          );
        })}
      </div>
      {hint && !hintUnterLabel && <span className={l.hint}>{hint}</span>}
    </div>
  );
}

export default OptionChips;
